import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { companyService } from '@/data/companyService';
import { Company } from '@/types/Company.interface';
import { validateTaxId } from '@/utils/taxIdValidator';
import { hasTaxSetupSupport, taxSetupErrorMessage } from '@/utils/b2bTaxErrors';
import { useUserViewMode } from '@/hooks/useUserViewMode';

interface CompanyTaxInfoCardProps {
  company: Company;
  onSaved: (company: Company) => void;
}

/**
 * The company as the taxpayer of its B2B sales (VKN/TCKN + tax office).
 * Only a backend with the B2B tax setup returns `taxIdNumber`, so the card
 * is hidden otherwise (an older backend would refuse the fields). Editable
 * for the system admin and the company admin of this company; the backend
 * audits every change and cannot clear a value, so there is no clear action.
 */
const CompanyTaxInfoCard = ({ company, onSaved }: CompanyTaxInfoCardProps) => {
  const { t } = useTranslation();
  const { isSuperAdmin, isCompanyAdmin } = useUserViewMode();
  const canEdit = isSuperAdmin || isCompanyAdmin;

  const [taxIdNumber, setTaxIdNumber] = useState(company.taxIdNumber ?? '');
  const [taxOffice, setTaxOffice] = useState(company.taxOffice ?? '');
  const [errors, setErrors] = useState<{
    taxIdNumber?: string;
    taxOffice?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    setTaxIdNumber(company.taxIdNumber ?? '');
    setTaxOffice(company.taxOffice ?? '');
    setErrors({});
  }, [company.id, company.taxIdNumber, company.taxOffice]);

  if (!hasTaxSetupSupport(company, 'taxIdNumber')) return null;

  const cleanedId = taxIdNumber.replace(/\s/g, '');
  const changed =
    cleanedId !== (company.taxIdNumber ?? '') ||
    taxOffice.trim() !== (company.taxOffice ?? '');

  const validate = (): boolean => {
    const next: typeof errors = {};
    const result = validateTaxId(cleanedId);
    if (!result.isValid) {
      next.taxIdNumber = t(result.errorKey ?? 'invalid.tax.id');
    }
    if (!taxOffice.trim()) next.taxOffice = t('tax.office.required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setFeedback(null);
    if (!validate()) return;
    try {
      setSaving(true);
      // Only what changed goes out: the backend audits each tax write.
      const formData = new FormData();
      if (cleanedId !== (company.taxIdNumber ?? '')) {
        formData.append('taxIdNumber', cleanedId);
      }
      if (taxOffice.trim() !== (company.taxOffice ?? '')) {
        formData.append('taxOffice', taxOffice.trim());
      }
      const updated = await companyService.update(company.id, formData);
      onSaved({
        ...company,
        taxIdNumber: updated?.taxIdNumber ?? cleanedId,
        taxOffice: updated?.taxOffice ?? taxOffice.trim()
      });
      setFeedback({
        severity: 'success',
        message: t('company.tax.saved')
      });
    } catch (error: any) {
      setFeedback({
        severity: 'error',
        message: taxSetupErrorMessage(error, t, t('company.tax.saveError'))
      });
    } finally {
      setSaving(false);
    }
  };

  const missing = !company.taxIdNumber;

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('company.tax.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('company.tax.description')}
        </Typography>

        {missing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('company.tax.missing')}
          </Alert>
        )}
        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2 }}>
            {feedback.message}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('tax.id.number')}
              value={taxIdNumber}
              onChange={(e) => setTaxIdNumber(e.target.value)}
              disabled={!canEdit || saving}
              error={!!errors.taxIdNumber}
              helperText={errors.taxIdNumber || t('tax.id.helper.text')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('tax.office')}
              value={taxOffice}
              onChange={(e) => setTaxOffice(e.target.value)}
              disabled={!canEdit || saving}
              error={!!errors.taxOffice}
              helperText={errors.taxOffice}
            />
          </Grid>
        </Grid>

        {canEdit && (
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !changed}
              startIcon={saving ? <CircularProgress size={18} /> : undefined}
            >
              {t('save')}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyTaxInfoCard;
